import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import Catalog from './pages/Catalog';
import AboutUs from './pages/AboutUs';
import Contact from './pages/Contact';
import AdminProductManager from './pages/AdminProductManager';
import AdminPromotions from './pages/AdminPromotions';
import AdminStatisticsPage from './pages/AdminStatisticsPage';
import AdminDeliveryPage from './pages/AdminDeliveryPage';
import AdminReports from './pages/AdminReports';
import ProductDetail from './pages/ProductDetail';
import CartPage from './pages/CartPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import CheckoutPage from './pages/CheckoutPage';
import AdminOrdersPage from './pages/AdminOrdersPage';
import SuperAdminPage from './pages/SuperAdminPage';
import AdminRewardsPage from './pages/AdminRewardsPage';
import AdminCustomersPage from './pages/AdminCustomersPage';
import AdminMessagesPage from './pages/AdminMessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import ReturnExchangePolicy from './pages/ReturnExchangePolicy';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import AdminReviewsPage from './pages/AdminReviewsPage';
import AdminCustomerConcerns from './pages/AdminCustomerConcerns';
import ProtectedRoute from './components/ProtectedRoute';
import AutoLogoutWarning from './components/AutoLogoutWarning';
import AdminSidebar from './components/AdminSidebar';
import autoLogout from './utils/autoLogout';

function AppContent() {
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if current route is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Scroll to top on route change - multiple methods for compatibility
  useEffect(() => {
    // Method 1: Standard scrollTo
    window.scrollTo(0, 0);
    
    // Method 2: documentElement scroll
    document.documentElement.scrollTop = 0;
    
    // Method 3: body scroll
    document.body.scrollTop = 0;
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) return;

    // Define public routes that don't require login
    const publicRoutes = ['/', '/home', '/catalog', '/about', '/contact', '/return-policy', '/product'];

    // Initialize auto-logout
    autoLogout.start({
      onWarning: () => {
        setShowLogoutWarning(true);
      },
      onLogout: () => {
        setShowLogoutWarning(false);
        
        // Only redirect to login if user is on a protected route
        const currentPath = window.location.pathname;
        const isPublicRoute = publicRoutes.some(route => 
          currentPath === route || currentPath.startsWith(route + '/')
        );
        
        if (!isPublicRoute) {
          navigate('/login?reason=inactivity');
        }
        // If on public route, just clear the token and let them continue browsing
      }
    });

    // Cleanup on unmount
    return () => {
      autoLogout.stop();
    };
  }, [navigate]);

  // Reset auto-logout when user logs in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && location.pathname !== '/login') {
      autoLogout.resetTimer();
    }
  }, [location.pathname]);

  const handleExtendSession = () => {
    console.log('Extending session - resetting timer');
    setShowLogoutWarning(false);
    const extended = autoLogout.extendSession();
    console.log('Session extended:', extended);
  };

  const handleLogoutNow = () => {
    setShowLogoutWarning(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('role');
    localStorage.removeItem('lastActivity');
    navigate('/login');
  };

  return (
    <>
      {/* Render sidebar for admin routes on desktop */}
      {isAdminRoute && <AdminSidebar />}
      
      <div className={isAdminRoute ? 'admin-main-content' : ''}>
        <AutoLogoutWarning 
          show={showLogoutWarning}
          onExtend={handleExtendSession}
          onLogout={handleLogoutNow}
        />
        <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/home" element={<Home />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/return-policy" element={<ReturnExchangePolicy />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        
        {/* Protected User Routes */}
        <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-failed" element={<PaymentFailed />} />
        
        {/* Protected Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute adminOnly={true}><AdminStatisticsPage /></ProtectedRoute>} />
        <Route path="/admin/products" element={<ProtectedRoute adminOnly={true}><AdminProductManager /></ProtectedRoute>} />
        <Route path="/admin/promos" element={<ProtectedRoute adminOnly={true}><AdminPromotions /></ProtectedRoute>} />
  <Route path="/admin/delivery" element={<ProtectedRoute adminOnly={true}><AdminDeliveryPage /></ProtectedRoute>} />
        <Route path="/admin/orders" element={<ProtectedRoute adminOnly={true}><AdminOrdersPage /></ProtectedRoute>} />
        <Route path="/admin/rewards" element={<ProtectedRoute adminOnly={true}><AdminRewardsPage /></ProtectedRoute>} />
        <Route path="/admin/customers" element={<ProtectedRoute adminOnly={true}><AdminCustomersPage /></ProtectedRoute>} />
        <Route path="/admin/messages" element={<ProtectedRoute adminOnly={true}><AdminMessagesPage /></ProtectedRoute>} />
        <Route path="/admin/customer-concerns" element={<ProtectedRoute adminOnly={true}><AdminCustomerConcerns /></ProtectedRoute>} />
        <Route path="/admin/notifications" element={<ProtectedRoute adminOnly={true}><AdminMessagesPage /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute adminOnly={true}><AdminReports /></ProtectedRoute>} />
        <Route path="/admin/super" element={<ProtectedRoute adminOnly={true}><SuperAdminPage /></ProtectedRoute>} />
        <Route path="/admin/reviews" element={<ProtectedRoute adminOnly={true}><AdminReviewsPage /></ProtectedRoute>} />
      </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
